import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Shell } from "@/components/Shell";
import { assetUrl, useSession } from "@/contexts/SessionContext";
import { OkFbiEmblem, Fade } from "@/components/Emblem";
import { Fingerprint, Dna, Camera, Mic, FileText, Smartphone, FlaskConical, Search, MapPin, StickyNote, ChevronLeft, ArrowLeft, ShieldAlert, CheckCircle2, XCircle, Lock } from "lucide-react";
import { toast } from "sonner";
import { ForensicLab, SnapshotsPanel } from "@/components/ForensicLab";
import { CCTVControlRoom } from "@/components/CCTVControlRoom";
import { LeaderboardPanel } from "@/components/Leaderboard";
import { CaseAnalyticsButton } from "@/components/CaseAnalytics";
import { CaseBackupButton } from "@/components/CaseBackup";
import { useAuth } from "@/pages/AuthPage";

const LAB_TOOLS = { fp: "fingerprint", dna: "dna", audio: "audio", docs: "documents", phone: "phone", mat: "materials" };

const TOOL_IMG = {
  fp: "/generated_images/tool-fingerprint.png",
  dna: "/generated_images/tool-dna.png",
  cctv: "/generated_images/tool-cctv.png",
  audio: "/generated_images/tool-audio.png",
  docs: "/generated_images/tool-documents.png",
  phone: "/generated_images/tool-phone.png",
  mat: "/generated_images/tool-material.png",
  scene: "/generated_images/tool-scene.png",
  notes: "/generated_images/tool-notes.png",
};

const TOOLS = [
  { icon: Search, label: "فحص البصمات", key: "fp" },
  { icon: Dna, label: "تحليل DNA", key: "dna" },
  { icon: Camera, label: "كاميرات المراقبة", key: "cctv" },
  { icon: Mic, label: "تحليل الصوت", key: "audio" },
  { icon: FileText, label: "فحص الوثائق", key: "docs" },
  { icon: Smartphone, label: "تحليل الهاتف", key: "phone" },
  { icon: FlaskConical, label: "فحص المواد", key: "mat" },
  { icon: MapPin, label: "مسرح الجريمة", key: "scene" },
  { icon: StickyNote, label: "الملاحظات", key: "notes" },
];

export default function Dashboard() {
  const [cases, setCases] = useState([]);
  const [caseRelease, setCaseRelease] = useState(null);   // null = not loaded yet; {} = loaded but empty
  const [lab, setLab] = useState(null); // { tool, caseId }
  const [cctvOpen, setCctvOpen] = useState(false);
  const { session } = useSession();
  const { user, isOwner } = useAuth();
  useEffect(() => { api.get("/cases?include_locked=true").then((r) => setCases(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/case-release").then((r) => setCaseRelease(r.data || {})).catch(() => setCaseRelease({})); }, []);
  // Filter cases by user's assigned_cases AND case_release.available (OWNER always sees all).
  // While `caseRelease` is still `null` (fetch in flight) we treat allowedCases as `null`
  // so the UI can render a proper loading state — never a false empty state.
  const allowedCases = React.useMemo(() => {
    if (!user) return null;
    if (isOwner) return cases;
    if (caseRelease === null) return null;   // still loading — do not filter yet
    const assigned = user.assigned_cases;
    const bySet = (assigned === "all" || assigned == null) ? cases
      : cases.filter((c) => (Array.isArray(assigned) ? assigned.includes(c.id) : false));
    return bySet.filter((c) => {
      const cr = caseRelease[c.id] || {};
      return cr.status !== "HIDDEN";
    });
  }, [cases, user, isOwner, caseRelease]);
  const activeCase = session?.current_case_id || "case-001";
  const activeCaseData = cases.find(c => c.id === activeCase);
  const [caseSuspects, setCaseSuspects] = useState([]);
  // Only fetch suspects if the active case is actually IN the allowed set —
  // otherwise the backend returns 403 (case_locked) and the unhandled promise
  // rejection triggers a global React runtime-error overlay for non-OWNERs.
  useEffect(() => {
    if (!activeCase) return;
    if (allowedCases === null) return;   // wait until we know
    const allowedIds = new Set(allowedCases.map(c => c.id));
    if (!isOwner && !allowedIds.has(activeCase)) { setCaseSuspects([]); return; }
    api.get(`/cases/${activeCase}`)
       .then(r => setCaseSuspects(r.data.suspects || []))
       .catch(() => setCaseSuspects([])); // graceful — no runtime overlay
  }, [activeCase, allowedCases, isOwner]);

  return (
    <Shell>
      <Fade>
        {/* Top strip - welcome + shield */}
        <div className="flex items-start justify-between mb-6 gap-4">
          <div>
            <div className="text-[10px] tracking-[0.3em] text-zinc-500 mb-1">DIGITAL INVESTIGATION HEADQUARTERS</div>
            <h1 className="font-cairo text-3xl lg:text-4xl font-black text-white leading-tight">مرحبًا يا محقق</h1>
            <div className="text-sm text-zinc-500 mt-1">اختر قضية للبدء بالتحقيق</div>
          </div>
          <div className="hidden lg:flex flex-col items-end">
            <div className="stat-chip"><ShieldAlert size={12} /> 05 قضايا مفتوحة</div>
          </div>
        </div>

        {/* CASES TITLE */}
        <div className="flex items-center gap-3 mb-4">
          <div className="h-px bg-gradient-to-l from-gold/60 to-transparent flex-1" />
          <h2 className="font-cairo text-xl font-bold text-white tracking-wide">القضايا المتاحة</h2>
          <div className="h-px bg-gradient-to-r from-gold/60 to-transparent flex-1" />
        </div>

        {/* Cases grid - large cinematic cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 mb-8">
          {allowedCases === null ? (
            // Loading state — /cases or /case-release still in flight.
            // Render skeleton placeholders so the page doesn't flash empty.
            <>
              {[0,1,2].map(i => (
                <div key={i} className="panel h-60 animate-pulse bg-zinc-900/40 border border-zinc-800/60" />
              ))}
            </>
          ) : allowedCases.length === 0 ? (
            <div className="col-span-full text-center text-zinc-500 py-12 font-cairo" data-testid="dashboard-cases-empty">
              لا توجد قضايا متاحة لك حالياً. تواصل مع الإدارة إذا كنت تتوقع رؤية قضايا هنا.
            </div>
          ) : (
            allowedCases.map((c, i) => <CaseCard key={c.id} c={c} delay={i * 0.06} caseRelease={caseRelease} isOwner={isOwner} />)
          )}
        </div>

        {/* Homepage cleanup (production correction pass):
            Removed generic Forensic Kit strip, Participants widget, Session
            Snapshots, and Top-Investigators duplicate. Forensic tools now
            live inside each opened case; participants live inside the
            Streamer live controls; leaderboard has its own dedicated page. */}
      </Fade>
      {lab && <ForensicLab open onClose={() => setLab(null)} tool={lab.tool} caseId={lab.caseId} suspects={caseSuspects} />}
      <CCTVControlRoom open={cctvOpen} onClose={() => setCctvOpen(false)} caseId={activeCase} suspects={caseSuspects} />
    </Shell>
  );
}

function CaseCard({ c, delay, caseRelease = {}, isOwner = false }) {
  const result = c.case_result;  // { status: 'solved'|'failed', revealed } or null
  const status = c.status || "جديدة";
  const rel = caseRelease?.[c.id] || {};
  const isLocked = Boolean(c.is_locked || rel.is_locked || rel.status === "UPCOMING_LOCKED" || (rel.available === false && rel.status !== "HIDDEN"));

  const handleCardClick = (e) => {
    if (isLocked && !isOwner) {
      e.preventDefault();
      e.stopPropagation();
      toast.info("هذه القضية مقفلة حالياً وستكون متاحة للتحقيق قريباً!", {
        icon: "🔒",
        description: "يعمل فريق المحققين على استكمال ملفات القضية."
      });
    }
  };

  const statusColor = result?.status === "solved" ? "text-emerald-400 border-emerald-700"
    : result?.status === "failed" ? "text-red-400 border-red-800"
    : isLocked ? "text-gold border-gold/60"
    : status === "قيد التحقيق" ? "text-gold border-gold/50"
    : "text-zinc-400 border-zinc-800";

  return (
    <Fade delay={delay}>
      <Link to={isLocked && !isOwner ? "#" : `/cases/${c.id}`} onClick={handleCardClick} data-testid={`case-card-${c.id}`}
        className={`block panel overflow-hidden hover:border-gold transition-all duration-300 group relative ${result?.status === "solved" ? "ring-1 ring-emerald-800/40" : result?.status === "failed" ? "ring-1 ring-red-900/50" : isLocked ? "border-gold/40 hover:border-gold/70" : ""}`}>
        {/* Case cover with cinematic overlay */}
        <div className="relative h-56 overflow-hidden bg-zinc-950">
          <img src={assetUrl(c.cover || c.scene) || "https://images.unsplash.com/photo-1522798514-97ceb8c4f1c8?w=1200&h=800&fit=crop"} alt={c.title}
            className="w-full h-full object-cover opacity-90 group-hover:scale-105 transition-transform duration-700"
            onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1522798514-97ceb8c4f1c8?w=1200&h=800&fit=crop"; }} />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
          <div className="absolute inset-0 border border-white/5" />

          {/* Case code chip - top right (RTL) */}
          <div className="absolute top-3 right-3 flex items-center gap-2">
            <div className="bg-black/85 border border-gold text-gold text-[10px] px-2 py-0.5 font-mono-ar tracking-wider">CASE {c.code}</div>
          </div>

          {/* Result / status chip - top left */}
          <div className={`absolute top-3 left-3 text-[10px] px-2.5 py-0.5 border bg-black/85 ${statusColor} font-mono-ar tracking-wider flex items-center gap-1.5`} data-testid={`case-status-${c.id}`}>
            {isLocked ? (
              <>
                <Lock size={11} className="text-gold" />
                <span className="font-cairo font-bold text-gold">قريباً</span>
              </>
            ) : result?.status === "solved" ? (
              <>
                <CheckCircle2 size={12} className="text-emerald-400" />
                <span>تم حل القضية</span>
              </>
            ) : result?.status === "failed" ? (
              <>
                <XCircle size={12} className="text-red-400" />
                <span>لم يتم حل القضية</span>
              </>
            ) : (
              <span>{status.toUpperCase()}</span>
            )}
          </div>

          {/* Center lock badge on upcoming cases */}
          {isLocked && (
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 rounded-full bg-black/85 border border-gold/60 backdrop-blur flex flex-col items-center justify-center shadow-[0_0_30px_rgba(0,0,0,0.85)] z-10" data-testid={`locked-overlay-${c.id}`}>
              <Lock size={26} className="text-gold mb-0.5" />
              <span className="text-[10px] font-cairo text-gold font-bold">قريباً</span>
            </div>
          )}

          {/* Big center result emblem on completed cases */}
          {!isLocked && result?.status === "solved" && (
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-24 rounded-full bg-emerald-950/70 border-2 border-emerald-600 backdrop-blur flex items-center justify-center shadow-[0_0_40px_rgba(16,185,129,0.35)]" data-testid={`solved-emblem-${c.id}`}>
              <CheckCircle2 size={54} className="text-emerald-400" strokeWidth={1.5} />
            </div>
          )}
          {!isLocked && result?.status === "failed" && (
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-24 rounded-full bg-red-950/70 border-2 border-red-700 backdrop-blur flex items-center justify-center shadow-[0_0_40px_rgba(220,38,38,0.35)]" data-testid={`failed-emblem-${c.id}`}>
              <XCircle size={54} className="text-red-400" strokeWidth={1.5} />
            </div>
          )}

          {/* Title inside image */}
          <div className="absolute bottom-3 right-4 left-4">
            <h3 className="font-cairo text-3xl font-black text-white drop-shadow-lg leading-tight mb-1">{c.title}</h3>
            <div className="text-xs text-zinc-300/90">{c.subtitle}</div>
          </div>
        </div>
        {/* Meta strip */}
        <div className={`px-4 py-3 border-t ${result?.status === "solved" ? "border-emerald-900/50 bg-emerald-950/10" : result?.status === "failed" ? "border-red-900/50 bg-red-950/10" : isLocked ? "border-gold/30 bg-black/60" : "border-metal bg-black/40"}`}>
          <div className="grid grid-cols-3 gap-2 text-[10px] mb-3">
            <MetaCell icon={MapPin} label={(c.location || "مسرح الجريمة").split(" - ")[0]} />
            <MetaCell label={c.date || "—"} />
            <MetaCell label={c.difficulty || "متوسطة"} />
          </div>
          <div className="flex items-center justify-between text-[10px] text-zinc-500 mb-1.5">
            <span>التقدم</span>
            <span className="text-gold font-mono-ar">{isLocked ? "0%" : `${c.progress || 0}%`}</span>
          </div>
          <div className="w-full h-0.5 bg-zinc-900 overflow-hidden">
            <div className={`h-full ${result?.status === "solved" ? "bg-gradient-to-l from-emerald-500 to-emerald-700" : result?.status === "failed" ? "bg-gradient-to-l from-red-500 to-red-800" : isLocked ? "bg-zinc-800" : "bg-gradient-to-l from-yellow-500 to-yellow-700"}`} style={{ width: isLocked ? "0%" : `${result ? 100 : (c.progress || 0)}%` }} />
          </div>
          <div className="mt-3 flex items-center justify-between">
            <div className="flex items-center gap-3 text-[10px] text-zinc-400">
              <span>{c.suspects_count || 0} مشتبه</span>
              <span className="text-zinc-700">|</span>
              <span>{c.evidence_count || 0} دليل</span>
            </div>
            {isLocked ? (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={!isOwner}
                  data-testid={`case-start-disabled-${c.id}`}
                  className="flex items-center gap-1.5 text-zinc-400 bg-zinc-900/90 border border-zinc-800 px-3 py-1.5 rounded text-xs font-cairo cursor-not-allowed opacity-75"
                  title="القضية مقفلة حالياً — قريباً"
                >
                  <Lock size={12} className="text-amber-400" />
                  <span>ابدأ التحقيق</span>
                  <span className="text-[10px] text-amber-400/90 font-bold mr-1">(قريباً)</span>
                </button>
                {isOwner && (
                  <span className="text-[10px] text-gold/80 font-cairo flex items-center gap-0.5">
                    معاينة كمالك <ArrowLeft size={11} />
                  </span>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-1 text-gold text-xs font-cairo group-hover:gap-2 transition-all">
                <span>{result ? "افتح الملف" : "ابدأ التحقيق"}</span>
                <ArrowLeft size={14} />
              </div>
            )}
          </div>
          {/* Analytics quick access (host-only) */}
          {isOwner && (
            <div className="mt-2 flex gap-2 flex-wrap" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
              <CaseAnalyticsButton caseId={c.id} />
              <CaseBackupButton caseId={c.id} />
            </div>
          )}
        </div>
      </Link>
    </Fade>
  );
}

function MetaCell({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-1 text-zinc-400 truncate">
      {Icon && <Icon size={10} className="text-gold shrink-0" />}
      <span className="truncate">{label}</span>
    </div>
  );
}

function ParticipantsWidget() {
  const { session, addParticipant, removeParticipant } = useSession();
  const [name, setName] = useState("");
  const parts = session?.participants || [];
  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    try { await addParticipant(name.trim()); setName(""); } catch {}
  };
  return (
    <div className="panel p-5" data-testid="participants-widget">
      <div className="flex items-center justify-between mb-3">
        <div className="label-caps">المشاركون في البث</div>
        <div className="text-[10px] text-zinc-600 font-mono-ar">{parts.length} VIEWERS</div>
      </div>
      <form onSubmit={submit} className="flex gap-2 mb-3">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="اسم مشارك من البث..."
          data-testid="participant-name-input"
          className="flex-1 bg-black/50 border border-metal rounded-sm px-2 py-1.5 text-xs text-white outline-none focus:border-gold" />
        <button className="btn-primary text-xs py-1.5 px-3" data-testid="add-participant-btn">إضافة</button>
      </form>
      <div className="space-y-1 max-h-40 overflow-y-auto">
        {parts.length === 0 && <div className="text-[11px] text-zinc-600 text-center py-3">لم يُضف أحد بعد</div>}
        {parts.map((p) => (
          <div key={p.name} className="flex items-center justify-between px-2 py-1 hover:bg-white/5 rounded-sm group">
            <div className="flex items-center gap-2 text-xs">
              <span className="w-1.5 h-1.5 rounded-full bg-gold" />
              <span className="text-white">{p.name}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-gold font-mono-ar">{p.points || 0}</span>
              <button onClick={() => removeParticipant(p.name)} className="text-zinc-700 opacity-0 group-hover:opacity-100 hover:text-red-400 text-xs" data-testid={`remove-${p.name}`}>×</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
